import * as React from "react";

import { cn } from "@/lib/utils";

import { resetPassword } from "@/api/user";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import * as z from "zod";
import FormTemplate, { FormSectionTemplate } from "../form-template";
import { toast } from "../ui/use-toast";
import { isApiError } from "@/api/_client";

const formSchema = z
  .object({
    password: z.string().nonempty({
      message: "Required",
    }),
    confirmPassword: z.string().nonempty({
      message: "Required",
    }),
  })
  .superRefine(({ password, confirmPassword }, ctx) => {
    if (confirmPassword !== password) {
      ctx.addIssue({
        code: "custom",
        message: "The passwords did not match",
        path: ["confirmPassword"],
      });
    }
  });

type PasswordResetFormProps = React.HTMLAttributes<HTMLDivElement>;

export function PasswordResetForm({
  className,
  ...props
}: PasswordResetFormProps) {
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | undefined>();
  const { uidb64, token } = useParams();

  const mutation = useMutation({
    mutationFn: async ({ password }: { password: string }) => {
      setError(undefined);
      const res = await resetPassword(uidb64!, token!, password);
      if (isApiError(res)) {
        if (res.formErrors.password) {
          setError(res.formErrors.password);
        } else if (res.error) {
          setError(res.error)
        } else {
          setError(
            "There was an error resetting your password. This could be because the reset link has expired or has already been used. Please try again.",
          );
        }
      } else {
        toast({
          title: "Password reset",
          description: "Your password has been reset",
        });
        navigate("/");
      }
    },
  });

  const passwordResetTemplate: FormSectionTemplate[] = [
    {
      fields: [
        {
          name: "password",
          showTopLabel: true,
          label: "Password",
          placeholder: "Enter your new password",
          type: "password",
        },
        {
          name: "confirmPassword",
          label: "Confirm password",
          showTopLabel: true,
          type: "password",
          placeholder: "Confirm your new password",
        },
      ],
    },
  ];

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
  });

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      {error && <div className="text-sm text-red-500">{error}</div>}
      <FormTemplate
        form={form}
        sections={passwordResetTemplate}
        onSubmit={mutation.mutateAsync}
        isLoading={mutation.isPending}
        submitLabel="Reset password"
      />
    </div>
  );
}
